import { get, post, patch } from '../../apiClient';

const margDataService = {
  /**
   * Get all orders from Marg
   */
  getOrders: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.companyId) queryParams.append('companyId', params.companyId);
      if (params.search) queryParams.append('search', params.search);
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.type) queryParams.append('type', params.type);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      if (params.onlyWithOrderId) queryParams.append('onlyWithOrderId', 'true');
      if (params.view) queryParams.append('view', params.view);
      if (params.shipmentStatus) queryParams.append('shipmentStatus', params.shipmentStatus);

      const url = `/crm/marg/orders?${queryParams.toString()}`;
      const response = await get(url);

      return {
        success: response.responseCode === 200,
        orders: response.data?.orders || [],
        pagination: response.data?.pagination || {}
      };
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch orders',
        orders: [],
        pagination: {}
      };
    }
  },

  /**
   * Get dispatch stats for the dispatch tracking page
   */
  getDispatchStats: async (params = {}) => {
    try {
      // Commented out: companyId parameter handling for future reference
      /*
      const queryParams = new URLSearchParams();
      if (params.companyId) queryParams.append('companyId', params.companyId);
      const url = `/crm/marg/orders/dispatch-stats?${queryParams.toString()}`;
      */

      const url = `/crm/marg/orders/dispatch-stats`;
      const response = await get(url);

      return {
        success: response.responseCode === 200,
        stats: response.data?.stats || {
          inTransit: 0,
          outForDelivery: 0,
          delivered: 0
        }
      };
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch dispatch stats',
        stats: {
          inTransit: 0,
          outForDelivery: 0,
          delivered: 0
        }
      };
    }
  },

  /**
   * Get analytics for Marg orders (total orders, total revenue)
   * Independent from list filters on the frontend.
   */
  getOrdersAnalytics: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.companyId) queryParams.append('companyId', params.companyId);
      if (params.dateRange) queryParams.append('dateRange', params.dateRange);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);

      const url = `/crm/marg/orders/analytics?${queryParams.toString()}`;
      const response = await get(url);

      return {
        success: response.responseCode === 200,
        totalOrders: response.data?.totalOrders ?? 0,
        totalRevenue: response.data?.totalRevenue ?? 0
      };
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch orders analytics',
        totalOrders: 0,
        totalRevenue: 0
      };
    }
  },

  /**
   * Get order by ID
   */
  getOrderById: async (orderId, companyId) => {
    try {
      const queryParams = new URLSearchParams();
      if (companyId) queryParams.append('companyId', companyId);

      const url = `/crm/marg/orders/${orderId}?${queryParams.toString()}`;
      const response = await get(url);

      return {
        success: response.responseCode === 200,
        order: response.data
      };
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch order'
      };
    }
  },

  /**
   * Get inventory/stock from Marg
   */
  getInventory: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.companyId) queryParams.append('companyId', params.companyId);
      if (params.search) queryParams.append('search', params.search);
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.gCode) queryParams.append('gCode', params.gCode);
      if (params.pid) queryParams.append('pid', params.pid);
      if (params.status) queryParams.append('status', params.status);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      if (params.hasBatchAndExpiry !== undefined) queryParams.append('hasBatchAndExpiry', params.hasBatchAndExpiry);
      if (params.expiryStatus) queryParams.append('expiryStatus', params.expiryStatus);

      const url = `/crm/marg/inventory?${queryParams.toString()}`;

      const response = await get(url);

      // Handle different response structures
      let inventory = [];
      let pagination = {};

      if (response && response.responseCode === 200) {
        if (response.data && response.data.inventory) {
          inventory = response.data.inventory;
          pagination = response.data.pagination || {};
        } else if (Array.isArray(response.data)) {
          inventory = response.data;
        } else if (response.inventory) {
          inventory = response.inventory;
          pagination = response.pagination || {};
        }
      }



      return {
        success: response.responseCode === 200,
        inventory: inventory,
        pagination: pagination
      };
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch inventory',
        inventory: [],
        pagination: {}
      };
    }
  },

  /**
   * Get analytics for Marg inventory (total products, low stock, out of stock, total value)
   * Independent from list filters on the frontend.
   */
  getInventoryAnalytics: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.companyId) queryParams.append('companyId', params.companyId);
      if (params.dateRange) queryParams.append('dateRange', params.dateRange);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);

      const url = `/crm/marg/inventory/analytics?${queryParams.toString()}`;
      const response = await get(url);

      return {
        success: response.responseCode === 200,
        totalProducts: response.data?.totalProducts ?? 0,
        lowStockItems: response.data?.lowStockItems ?? 0,
        outOfStockItems: response.data?.outOfStockItems ?? 0,
        totalValue: response.data?.totalValue ?? 0,
        // Batch Expiry Stats
        totalBatches: response.data?.totalBatches ?? 0,
        activeBatches: response.data?.activeBatches ?? 0,
        expiringSoon: response.data?.expiringSoon ?? 0,
        expiredBatches: response.data?.expiredBatches ?? 0
      };
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch inventory analytics',
        totalProducts: 0,
        lowStockItems: 0,
        outOfStockItems: 0,
        totalValue: 0
      };
    }
  },

  /**
   * Get products from Marg
   */
  getProducts: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.companyId) queryParams.append('companyId', params.companyId);
      if (params.search) queryParams.append('search', params.search);
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.gCode) queryParams.append('gCode', params.gCode);

      const url = `/crm/marg/products?${queryParams.toString()}`;
      const response = await get(url);

      return {
        success: response.responseCode === 200,
        products: response.data?.products || [],
        pagination: response.data?.pagination || {}
      };
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch products',
        products: [],
        pagination: {}
      };
    }
  },

  /**
   * Update composition for a Marg product
   */
  updateProductComposition: async (pid, payload = {}) => {
    try {
      if (!pid) {
        throw new Error('Product ID is required');
      }

      const response = await patch(`/crm/marg/products/${pid}/composition`, payload);

      return {
        success: response.responseCode === 200,
        data: response.data || null,
        message: response.responseMessage || 'Composition updated successfully'
      };
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to update composition'
      };
    }
  },

  /**
   * Sync masters (products, stock, etc.) from Marg
   */
  syncMasters: async (companyId) => {
    try {

      // Only include companyId in body if it's provided (not null/undefined)
      const body = companyId ? { companyId } : {};
      const response = await post('/crm/marg/sync/masters', body, {
        timeout: 10 * 60 * 1000
      });
      return {
        success: response.success !== false,
        ...response
      };
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to sync masters'
      };
    }
  },

  /**
   * Sync transactions (orders) from Marg
   */
  syncTransactions: async (companyId) => {
    try {

      // Only include companyId in body if it's provided (not null/undefined)
      const body = companyId ? { companyId } : {};
      const response = await post('/crm/marg/sync/incremental', body, {
        timeout: 10 * 60 * 1000
      });
      return {
        success: response.success !== false,
        ...response
      };
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to sync transactions'
      };
    }
  },

  /**
   * Get shipment details for a specific order by orderId
   */
  getOrderShipmentByOrderId: async (orderId) => {
    try {
      if (!orderId) {
        throw new Error('Order ID is required');
      }

      const url = `/crm/marg/orders/shipment/${orderId}`;
      const response = await get(url);

      return {
        success: response.responseCode === 200,
        shipment: response.data || null
      };
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch shipment details',
        shipment: null
      };
    }
  },

  /**
   * Sync API Type 3 (MDis - Bill-wise details) from Marg
   */
  syncApiType3: async (companyId) => {
    try {

      // Only include companyId in body if it's provided (not null/undefined)
      const body = companyId ? { companyId } : {};
      const response = await post('/crm/marg/sync/api-type-3', body);
      return {
        success: response.success !== false,
        ...response
      };
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to sync API Type 3'
      };
    }
  }
};

export default margDataService;

